import { useState, useEffect, useCallback, useMemo } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
    ArrowLeft,
    Clock,
    CheckCircle,
    XCircle,
    Play,
    BookOpen,
    Award,
    HelpCircle,
    RefreshCcw,
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    Download,
    Lock,
    Loader2,
    FileQuestion,
    Sparkles
} from "lucide-react"
import { useQuiz } from "../hooks/useQuiz"
import { useQuizAttempt } from "../hooks/useQuizAttempt"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { courseSchema } from "../utils/schemas/cours-schema"
import { useAuth } from "../hooks/useAuth"
import AccessGuard from "../components/AccessGuard"
import { api } from "../utils/apiComponent/apiClient"
import { Button } from "../components/ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../components/Card"
import { Badge } from "../components/ui/badge"
import { showSuccessToast, showErrorToast } from "../utils/toast"
import { useQuizSecurity } from "../hooks/useQuizSecurity"
import { useTranslation } from "../i18n"
import { loadAndMergeTranslation } from "../utils/translationUtils"
import { CourseReviewModal } from "../components/CourseReviewModal"
import { CertificatePreview } from "../components/CertificatePreview"

export default function QuizPage() {
  const { courseId, moduleId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { tObj, t, language } = useTranslation()
  
  // Quiz states
  const [quizState, setQuizState] = useState('intro') // 'intro', 'in-progress', 'completed', 'submitting'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [answers, setAnswers] = useState({}) // Store user answers
  const [questionStartTimes, setQuestionStartTimes] = useState({}) // Track time spent per question
  const [quizStartTime, setQuizStartTime] = useState(null)
  const [score, setScore] = useState(0)
  const [attemptsMade, setAttemptsMade] = useState(0)
  const [apiResult, setApiResult] = useState(null) // Store API response
  const [apiMessages, setApiMessages] = useState([]) // Store API messages
  const [showCongratulationsModal, setShowCongratulationsModal] = useState(false) // Modal de félicitation
  const [showReviewModal, setShowReviewModal] = useState(false) // Modal d'évaluation du cours
  const [showCertificatePreview, setShowCertificatePreview] = useState(false) // Afficher le preview du certificat
  const [currentAttemptId, setCurrentAttemptId] = useState(null) // ID de la tentative en cours (si créée côté serveur)
  const [initialAttemptLock, setInitialAttemptLock] = useState(false) // Verrouillage après abandon
  const [startingQuiz, setStartingQuiz] = useState(false) // État de chargement lors du démarrage du quiz
  const [randomizedQuestions, setRandomizedQuestions] = useState([]) // Questions randomisées pour cette tentative

  // Fetch course details
  // NOTE: This is needed for AccessGuard component which requires:
  // - course.price and course.isFree to determine if course is free/paid (for auto-enrollment)
  // - course.title for user messages
  // - course.id for enrollment
  // TODO: Optimize by including minimal course info (price, isFree, title) in verify-access API response
  // to avoid this separate API call
  const {
    data: courseData,
    loading: courseLoading
  } = useGet(`${endpoints.courses}/${courseId}`, {
    schema: courseSchema
  })

  const [translatedCourse, setTranslatedCourse] = useState(null)

  // Charger la traduction du cours
  useEffect(() => {
    if (courseData && language) {
      loadAndMergeTranslation('course', courseId, courseData, language).then(translated => {
        setTranslatedCourse(translated)
      })
    } else {
      setTranslatedCourse(courseData)
    }
  }, [courseData, courseId, language])

  const course = translatedCourse || courseData

  // Charger d'abord le quiz de base pour obtenir son ID
  const { quiz: baseQuiz, loading: baseQuizLoading, error: baseQuizError } = useQuiz(moduleId)
  
  // Charger le quiz complet avec traductions via l'API de traduction
  const quizTranslationEndpoint = baseQuiz?.id && language 
    ? endpoints.getQuizTranslation(baseQuiz.id, language)
    : null
  
    const {
      data: translatedQuizData,
      loading: translationLoading,
      error: translationError
    } = useGet(quizTranslationEndpoint, {
      immediate: !!quizTranslationEndpoint,
   
    })
  
  // Utiliser le quiz traduit si disponible, sinon le quiz de base
  const quiz = translatedQuizData || baseQuiz
  const loading = baseQuizLoading || translationLoading
  const error = baseQuizError || translationError
  
  // Extraire les informations de tentatives depuis l'API
  const attemptsInfo = quiz?.attemptsInfo || null
  const hasAttemptsAvailable = attemptsInfo?.hasAttemptsAvailable ?? true
  const ongoingAttempt = attemptsInfo?.ongoingAttempt || null
  // Si maxAttempts est null, les tentatives sont illimitées
  const isUnlimitedAttempts = quiz?.maxAttempts === null || quiz?.maxAttempts === undefined
  const remainingAttempts = attemptsInfo?.remainingAttempts ?? (isUnlimitedAttempts ? Infinity : (quiz?.maxAttempts || 1))
  const totalAttempts = attemptsInfo?.totalAttempts ?? 0
  
  const { submitAttempt, loading: submitting, error: submitError } = useQuizAttempt(quiz?.id)

  useEffect(() => {
    if (quizState !== 'intro') {
      return
    }

    // Ne verrouiller que si :
    // 1. Les tentatives ne sont PAS illimitées (maxAttempts !== null)
    // 2. ET pas de tentatives disponibles
    // 3. ET pas de tentative en cours à reprendre
    // Si les tentatives sont illimitées (maxAttempts === null), ne jamais verrouiller
    // Si une tentative en cours existe, l'utilisateur peut la reprendre
    if (!isUnlimitedAttempts && !hasAttemptsAvailable && !ongoingAttempt) {
      setInitialAttemptLock(true)
    } else {
      setInitialAttemptLock(false)
    }
  }, [quizState, isUnlimitedAttempts, hasAttemptsAvailable, ongoingAttempt])

  // Sécurité anti-triche : activer uniquement pendant le quiz
  const isQuizActive = quizState === 'in-progress' || quizState === 'submitting'
  const { tabSwitches, warnings, violations, isBlurred } = useQuizSecurity(
    isQuizActive,
    (violationType) => {
      // Logger les violations pour analyse (optionnel)
      console.warn('Violation de sécurité détectée:', violationType)
    }
  )

  // L'accès au quiz est vérifié via AccessGuard qui utilise GET /quiz/:id/verify-access
  // Cette API centralise toutes les vérifications :
  // - Accès au cours (enrollment, certificat, expiration)
  // - Accès au module (module précédent complété)
  // - Toutes les leçons complétées (via LessonProgress)
  // - Quiz non déjà validé

  // Fonction pour télécharger le certificat
  const handleDownloadCertificate = async (certificate) => {
    try {
      console.log("📥 Téléchargement du certificat:", certificate)
      
      if (!certificate) {
        throw new Error(tObj.quiz.certificateMissing())
      }
      
      const certificateId = certificate.id
      
      if (!certificateId) {
        throw new Error(tObj.quiz.certificateIdMissing())
      }
      
      // Appeler l'endpoint de téléchargement avec responseType: 'blob'
      const blob = await api.get<Blob>(endpoints.downloadCertificate(certificateId), {
        responseType: 'blob'
      })
      
      // Vérifier que blob est valide
      if (!blob || !(blob instanceof Blob)) {
        console.error("La réponse n'est pas un Blob valide:", blob)
        throw new Error(tObj.quiz.invalidFile())
      }
      
      // Vérifier que le blob n'est pas vide
      if (blob.size === 0) {
        throw new Error(tObj.quiz.emptyFile())
      }
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Générer un nom de fichier avec le titre du cours et la date (comme dans ProfileUserPage)
      const courseTitle = certificate.courseTitle || certificate.course?.title || course?.title || 'Certificat'
      const year = certificate.issuedAt ? new Date(certificate.issuedAt).getFullYear() : new Date().getFullYear()
      // Nettoyer le titre pour le nom de fichier (enlever caractères spéciaux)
      const cleanTitle = courseTitle.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_')
      link.download = `Certificat_${cleanTitle}_${year}.pdf`
      
      // Déclencher le téléchargement
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      showSuccessToast({ message: tObj.quiz.downloadSuccess() })
    } catch (error) {
      console.error("Erreur lors du téléchargement du certificat:", error)
      
      // Fallback : si pdfUrl est disponible, l'ouvrir dans un nouvel onglet
      if (certificate.pdfUrl) {
        window.open(certificate.pdfUrl, '_blank')
        return
      }
      
      const errorMessage = error?.message || tObj.quiz.downloadError()
      showErrorToast({ message: errorMessage })
    }
  }

  // Initialize timer when quiz starts
  useEffect(() => {
    if (quiz && quizState === 'intro') {
      // Ensure timeLimit is a valid number, default to 0 (no limit) if undefined/null
      const timeLimit = quiz.timeLimit != null && !isNaN(quiz.timeLimit) ? quiz.timeLimit : 0
      setTimeRemaining(timeLimit * 60) // Convert minutes to seconds
    }
  }, [quiz, quizState])

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const startQuiz = async () => {
    if (!quiz?.id) {
      showErrorToast({ message: 'Quiz non disponible' })
      return
    }

    if (initialAttemptLock) {
      showErrorToast({ message: tObj.quiz.attemptsExhausted() || 'Tentative verrouillée' })
      return
    }

    setStartingQuiz(true)
    try {
      // Appeler l'API pour démarrer le quiz (consomme une tentative)
      const response = await api.post(endpoints.startQuizAttempt(quiz.id))
      
      // L'API retourne { success: true, data: {...}, messages: [...] }
      // apiClient extrait déjà data, donc response contient directement les données ou response.data
      const responseData = response?.data || response
      const attemptData = responseData?.data || responseData
      
      if (!attemptData || !attemptData.id) {
        throw new Error('Réponse invalide de l\'API')
      }

      // Mettre à jour l'état avec les informations de la tentative
      setCurrentAttemptId(attemptData.id)
      
      // Si la tentative a déjà un startedAt, l'utiliser, sinon utiliser maintenant
      const startTime = attemptData.startedAt 
        ? new Date(attemptData.startedAt).getTime() 
        : Date.now()
      
      setQuizStartTime(startTime)
      setQuizState('in-progress')
      setAttemptsMade(prev => prev + 1)
      setCurrentQuestionIndex(0)
      setAnswers({})
      setInitialAttemptLock(false)
      
      // Randomiser les questions pour cette tentative
      if (quiz.questions && quiz.questions.length > 0) {
        const randomized = randomizeQuestions(quiz.questions)
        setRandomizedQuestions(randomized)
        
        // Initialiser le temps de départ pour la première question randomisée
        if (randomized.length > 0) {
          setQuestionStartTimes({ [randomized[0]?.id]: startTime })
        }
      } else {
        setRandomizedQuestions([])
      }

      // Afficher un message de succès si disponible
      const messages = responseData?.messages || response?.messages || []
      if (messages.length > 0) {
        showSuccessToast({ message: messages[0] })
      }

      // Si une tentative en cours existait déjà, informer l'utilisateur
      if (ongoingAttempt?.id === attemptData.id) {
        console.log('Tentative en cours récupérée:', attemptData.id)
      }
    } catch (error) {
      console.error('Erreur lors du démarrage du quiz:', error)
      
      // Gérer les erreurs spécifiques
      if (error?.status === 400) {
        showErrorToast({ 
          message: error?.message || 'Limite de tentatives atteinte' 
        })
      } else if (error?.status === 403) {
        showErrorToast({ 
          message: error?.message || 'Accès interdit. Vérifiez que tous les blocs sont complétés.' 
        })
      } else if (error?.status === 404) {
        showErrorToast({ 
          message: 'Quiz non trouvé' 
        })
      } else {
        showErrorToast({ 
          message: error?.message || 'Erreur lors du démarrage du quiz' 
        })
      }
    } finally {
      setStartingQuiz(false)
    }
  }

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }

  // Fonction pour masquer les bonnes réponses côté client (sécurité anti-triche)
  const sanitizeQuestions = useCallback((questions) => {
    if (!questions || !Array.isArray(questions)) return []
    
    try {
      return questions.map(question => ({
        ...question,
        options: Array.isArray(question.options) 
          ? question.options.map(option => {
              // Créer une copie de l'option sans le champ isCorrect
              const { isCorrect, ...sanitizedOption } = option
              return sanitizedOption
            })
          : []
      }))
    } catch (error) {
      console.error('Error sanitizing questions:', error)
      return []
    }
  }, [])

  // Questions sécurisées (sans les bonnes réponses) pour l'affichage
  // Utiliser des dépendances stables pour éviter les recalculs inutiles
  // Extraire quizQuestions de manière sécurisée
  const quizQuestions = (quiz && Array.isArray(quiz.questions)) ? quiz.questions : null
  const sanitizedQuestions = useMemo(() => {
    // Si pas de questions, retourner un tableau vide
    if (!quizQuestions || !Array.isArray(quizQuestions)) {
      return []
    }
    try {
      const sanitized = sanitizeQuestions(quizQuestions)
      // Garantir qu'on retourne toujours un tableau valide
      const result = Array.isArray(sanitized) ? sanitized : []
      // Vérifier que le résultat est sérialisable (pas de fonctions, etc.)
      return result
    } catch (error) {
      console.error('Error sanitizing questions:', error)
      return []
    }
  }, [quizQuestions, sanitizeQuestions])

  // Utiliser les questions randomisées et sanitizées pour l'affichage
  // IMPORTANT: Ce hook doit être appelé avant tous les retours conditionnels
  const randomizedSanitizedQuestions = useMemo(() => {
    // Toujours retourner un tableau valide, même en cas d'erreur
    let result = []
    
    try {
      // S'assurer que sanitizedQuestions est toujours un tableau
      const safeSanitizedQuestions = Array.isArray(sanitizedQuestions) ? sanitizedQuestions : []
      
      // S'assurer que randomizedQuestions est un tableau valide
      const safeRandomizedQuestions = Array.isArray(randomizedQuestions) ? randomizedQuestions : []
      
      if (safeRandomizedQuestions.length === 0 || quizState === 'intro') {
        // Si pas encore randomisées (état intro), utiliser l'ordre original
        result = safeSanitizedQuestions
      } else {
        // Sanitizer les questions randomisées
        const sanitized = sanitizeQuestions(safeRandomizedQuestions)
        // S'assurer que le résultat est toujours un tableau
        result = Array.isArray(sanitized) ? sanitized : safeSanitizedQuestions
      }
    } catch (error) {
      console.error('Error in randomizedSanitizedQuestions useMemo:', error)
      // En cas d'erreur, essayer de retourner sanitizedQuestions, sinon tableau vide
      result = Array.isArray(sanitizedQuestions) ? sanitizedQuestions : []
    }
    
    // Garantir qu'on retourne toujours un tableau valide
    return Array.isArray(result) ? result : []
  }, [randomizedQuestions, sanitizedQuestions, sanitizeQuestions, quizState])

  // Fonction pour randomiser les questions
  const randomizeQuestions = useCallback((questions) => {
    if (!questions || questions.length === 0) return []
    // Créer une copie du tableau pour ne pas modifier l'original
    const shuffled = [...questions]
    // Algorithme de Fisher-Yates pour mélanger
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
  }, [])

  // Fonction de calcul du score (désactivée côté client pour la sécurité)
  // Le score est maintenant calculé uniquement côté serveur
  const calculateScore = useCallback(() => {
    // Ne plus calculer le score côté client pour empêcher la triche
    // Le score sera toujours fourni par l'API après soumission
    return 0
  }, [])

  const handleQuizSubmit = useCallback(async () => {
    if (!quiz) return

    setQuizState('submitting')
    
    try {
      // Calculate time spent per question
      const now = Date.now()
      const totalTimeSpent = quizStartTime ? Math.floor((now - quizStartTime) / 1000) : 0 // Convert to seconds
      
      // Prepare answers in the required format
      // Utiliser les questions originales (quiz.questions) pour s'assurer que tous les IDs sont présents
      // même si l'ordre a été randomisé
      const formattedAnswers = quiz.questions.map(question => {
        const userAnswer = answers[question.id]
        const questionStartTime = questionStartTimes[question.id] || quizStartTime
        const timeSpent = Math.floor((now - questionStartTime) / 1000)
        
        // Format answer based on question type and user input
        let formattedAnswer
        if (question.type === 'MULTIPLE_CHOICE') {
          // For multiple choice, convert array to string or empty string
          if (Array.isArray(userAnswer) && userAnswer.length > 0) {
            formattedAnswer = userAnswer.join(',') // Join array elements with comma
          } else {
            formattedAnswer = '' // Empty string for no selection
          }
        } else {
          // For single choice and essay, use string directly
          formattedAnswer = userAnswer || ''
        }
        
        return {
          questionId: question.id,
          answer: formattedAnswer,
          timeSpent: Math.max(timeSpent, 1) // Ensure at least 1 second
        }
      })

      // Prepare the attempt data (userId handled by API, quizId in body for API validation)
      const attemptData = {
        quizId: quiz.id, // S'assurer que quizId est fourni dans le body
        answers: formattedAnswers,
        timeSpent: totalTimeSpent
      }

      // Debug: Log the data being sent
      console.log('📤 Données envoyées à l\'API:', JSON.stringify(attemptData, null, 2))
      console.log('📤 Formatted answers:', formattedAnswers)

      // Validate data before sending
      const isValidData = attemptData.answers.every(answer => 
        typeof answer.questionId === 'string' && 
        typeof answer.answer === 'string' && 
        typeof answer.timeSpent === 'number' && 
        answer.timeSpent > 0
      ) && typeof attemptData.timeSpent === 'number' && attemptData.timeSpent >= 0

      if (!isValidData) {
        console.error('❌ Données invalides détectées:', attemptData)
        throw new Error('Format de données invalide')
      }

      console.log('✅ Validation des données réussie')

      // Submit to API
      const response = await submitAttempt(attemptData)
      console.log('📥 Structure de réponse reçue:', response)
      
      // Use API result if available, otherwise fallback to local calculation
      // Handle both possible response structures
      let apiData = null
      let apiMessages = []
      
      if (response) {
        // Check if response has the expected structure
        if (response.data) {
          // Structure: { success, data: {...}, messages: [...] }
          apiData = response.data
          apiMessages = response.messages || []
        } else if (response.score !== undefined) {
          // Direct data structure: { score, maxScore, ... }
          apiData = response
        }
      }
      
      if (apiData && apiData.score !== undefined) {
        // Convert score to percentage: (score / maxScore) * 100
        const scorePercentage = apiData.maxScore > 0 ? Math.round((apiData.score / apiData.maxScore) * 100) : 0
        setScore(scorePercentage)
        setApiResult(apiData)
        setApiMessages(apiMessages)
      } else {
        // Fallback to local calculation
        const calculatedScore = calculateScore()
        setScore(calculatedScore)
      }
      
      setQuizState('completed')
      setCurrentAttemptId(null)
      setInitialAttemptLock(false)
      // Réinitialiser les questions randomisées après soumission
      setRandomizedQuestions([])
      
      // Si le quiz est réussi, déclencher un événement pour notifier les autres composants
      if (apiData?.isPassed) {
        // Déclencher un événement personnalisé pour notifier les autres composants
        window.dispatchEvent(new CustomEvent('quizPassed', {
          detail: { moduleId, courseId, quizId: quiz?.id }
        }))
        
        // Gérer les différents cas selon la progression du cours
        const courseProgress = apiData?.courseProgress
        const certificate = apiData?.certificate
        const nextModule = courseProgress?.nextModule
        
        if (certificate) {
          // Cas 1 : Certificat généré - Cours complété
          setShowCongratulationsModal(true)
          showSuccessToast({ 
            message: tObj.quiz.courseCompletedMessage()
          })
        } else if (courseProgress?.courseCompleted) {
          // Cas 2 : Cours complété sans certificat
          setShowCongratulationsModal(true)
          showSuccessToast({ 
            message: tObj.quiz.allModulesCompletedMessage()
          })
        } else if (nextModule) {
          // Cas 3 : Prochain module disponible
          showSuccessToast({ 
            message: t('quiz.excellentWork', { title: nextModule.title })
          })
        } else {
          // Cas par défaut : Quiz réussi
          showSuccessToast({ 
            message: tObj.quiz.quizValidated()
          })
        }
      }
        } catch (error) {
          console.error('Error submitting quiz:', error)
          
          // Check if it's a max attempts error
          if (error?.data?.errors?.some(err => err.includes('nombre maximum de tentatives'))) {
            // Handle max attempts reached
            setApiMessages(error.data.errors || [tObj.quiz.maxAttemptsError()])
            setScore(0) // No score for failed submission
            setQuizState('completed')
          } else {
            // Fallback to local calculation for other errors
            const calculatedScore = calculateScore()
            setScore(calculatedScore)
            setQuizState('completed')
          }
        }
  }, [quiz, answers, questionStartTimes, quizStartTime, submitAttempt, calculateScore])

  // Timer countdown with warnings
  useEffect(() => {
    let timer
    if (quizState === 'in-progress' && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prevTime => {
          const newTime = prevTime - 1
          
          // Show warnings at critical times
          if (newTime === 300) { // 5 minutes remaining
            console.log('⚠️ 5 minutes restantes !')
            // You could add a toast notification here
          } else if (newTime === 60) { // 1 minute remaining
            console.log('🚨 1 minute restante !')
            // You could add a more urgent notification here
          } else if (newTime === 10) { // 10 seconds remaining
            console.log('🚨 10 secondes restantes !')
          }
          
          if (newTime <= 0) {
            handleQuizSubmit() // Auto-submit when time runs out
            return 0
          }
          return newTime
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [quizState, timeRemaining, handleQuizSubmit])

  // Avertir l'utilisateur avant de quitter la page pendant le quiz
  useEffect(() => {
    if (quizState !== 'in-progress' && quizState !== 'submitting') {
      return
    }

    const handleBeforeUnload = (e) => {
      const message = tObj.quiz.leavingWarning()
      e.preventDefault()
      e.returnValue = message
      return message
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [quizState, tObj.quiz])

  // Gérer la navigation React Router (quand l'utilisateur clique sur un lien ou utilise le bouton retour)
  useEffect(() => {
    if (quizState !== 'in-progress' && quizState !== 'submitting') {
      return
    }

    // Intercepter le bouton retour du navigateur
    const handlePopState = () => {
      const confirmed = window.confirm(
        tObj.quiz.leavingConfirm()
      )
      
      if (!confirmed) {
        // Empêcher la navigation en repoussant l'état actuel
        window.history.pushState(null, '', window.location.pathname)
      }
    }

    // Intercepter les clics sur tous les liens
    const handleLinkClick = (e) => {
      const link = e.target.closest('a')
      if (link && link.href && !link.href.startsWith('javascript:') && !link.href.startsWith('#')) {
        // Vérifier si c'est un lien interne (même origine)
        try {
          const url = new URL(link.href)
          if (url.origin === window.location.origin) {
            const confirmed = window.confirm(
              tObj.quiz.leavingConfirm()
            )
            
            if (!confirmed) {
              e.preventDefault()
              e.stopPropagation()
              return false
            }
          }
        } catch {
          // Si l'URL n'est pas valide, laisser passer
        }
      }
    }

    // Empêcher la navigation initiale
    window.history.pushState(null, '', window.location.pathname)
    window.addEventListener('popstate', handlePopState)
    document.addEventListener('click', handleLinkClick, true)

    // Nettoyage
    return () => {
      window.removeEventListener('popstate', handlePopState)
      document.removeEventListener('click', handleLinkClick, true)
    }
  }, [quizState, tObj.quiz])

  const retakeQuiz = () => {
    // Rediriger vers le reader du cours (première leçon du module actuel)
    // Le reader déterminera automatiquement la première leçon si aucune n'est spécifiée
    navigate(`/courses/${courseId}/modules/${moduleId}`)
  }

  const goToNextQuestion = () => {
    const questionsLength = randomizedSanitizedQuestions?.length || 0
    if (currentQuestionIndex < questionsLength - 1) {
      const nextIndex = currentQuestionIndex + 1
      // Utiliser les questions randomisées pour obtenir l'ID
      const nextQuestionId = randomizedSanitizedQuestions?.[nextIndex]?.id
      
      // Track time when moving to next question
      if (nextQuestionId && !questionStartTimes[nextQuestionId]) {
        setQuestionStartTimes(prev => ({
          ...prev,
          [nextQuestionId]: Date.now()
        }))
      }
      
      setCurrentQuestionIndex(nextIndex)
    }
  }

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    }
  }

  // Function to convert option IDs to readable text
  const getReadableAnswer = (answer, question) => {
    if (!answer || !question || !question.options) {
      return answer || tObj.quiz.noAnswer()
    }

    if (question.type === 'SINGLE_CHOICE') {
      // Find the option with the matching ID
      const option = question.options.find(opt => opt.id === answer)
      return option ? option.text : answer
    } else if (question.type === 'MULTIPLE_CHOICE') {
      // Split comma-separated IDs and find corresponding texts
      const optionIds = answer.split(',').filter(id => id.trim())
      const optionTexts = optionIds.map(id => {
        const option = question.options.find(opt => opt.id === id.trim())
        return option ? option.text : id
      })
      return optionTexts.length > 0 ? optionTexts.join(', ') : tObj.quiz.noAnswer()
    } else {
      // For ESSAY questions, return the text as is
      return answer || tObj.quiz.noAnswer()
    }
  }

  // Function to convert API result answer to readable text using original quiz data
  const getReadableAnswerFromApiResult = (apiAnswer) => {
    if (!apiAnswer || !quiz || !quiz.questions) {
      return apiAnswer?.answer || tObj.quiz.noAnswer()
    }

    // Find the original question with options
    const originalQuestion = quiz.questions.find(q => q.id === apiAnswer.questionId)
    if (!originalQuestion) {
      return apiAnswer.answer || tObj.quiz.noAnswer()
    }

    return getReadableAnswer(apiAnswer.answer, originalQuestion)
  }

  // Function to generate a readable quiz title
  const getReadableQuizTitle = () => {
    if (!quiz) return tObj.quiz.quizTitle()

    // If the quiz has module information, use it
    if (quiz.module && quiz.module.title) {
      return t('quiz.quizOfModuleTitle', { title: quiz.module.title })
    }

    // If the title contains "Quiz - Module [ID]", make it more generic
    if (quiz.title && quiz.title.includes("Quiz - Module")) {
      return tObj.quiz.quizOfModule()
    }

    // Fallback to original title
    return quiz.title || tObj.quiz.quizTitle()
  }

  const renderQuestion = (question) => {
    const userAnswer = answers[question.id]

    switch (question.type) {
      case 'SINGLE_CHOICE':
        return (
          <div className="space-y-2" translate="no">
            {question.options.map((option, idx) => (
              <motion.label
                key={`${question.id}-${option.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05, duration: 0.3 }}
                whileHover={{ x: 4 }}
                className={`flex items-center p-4 border cursor-pointer transition-colors ${
                  userAnswer === option.id
                    ? 'border-[#203a8a] bg-[#1B2240] text-white'
                    : 'border-[#D5DAE3] hover:border-[#203a8a] bg-white text-[#1B2240]'
                }`}
              >
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option.id}
                  checked={userAnswer === option.id}
                  onChange={() => handleAnswerChange(question.id, option.id)}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                  className="sr-only"
                />
                <div className={`w-5 h-5 border mr-4 flex items-center justify-center flex-shrink-0 ${
                  userAnswer === option.id ? 'border-[#E8C547] bg-[#E8C547]' : 'border-[#D5DAE3]'
                }`}>
                  {userAnswer === option.id && (
                    <div className="w-2 h-2 bg-[#1B2240]"></div>
                  )}
                </div>
                <span className="flex-1">{option.text}</span>
              </motion.label>
            ))}
          </div>
        )

      case 'MULTIPLE_CHOICE':
        return (
          <div className="space-y-2" translate="no">
            {question.options.map((option, idx) => {
              const isSelected = Array.isArray(userAnswer) && userAnswer.includes(option.id)
              return (
                <motion.label
                  key={`${question.id}-${option.id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.3 }}
                  whileHover={{ x: 4 }}
                  className={`flex items-center p-4 border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-[#203a8a] bg-[#1B2240] text-white'
                      : 'border-[#D5DAE3] hover:border-[#203a8a] bg-white text-[#1B2240]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const currentAnswers = userAnswer || []
                      if (e.target.checked) {
                        handleAnswerChange(question.id, [...currentAnswers, option.id])
                      } else {
                        handleAnswerChange(question.id, currentAnswers.filter(id => id !== option.id))
                      }
                    }}
                    data-gramm="false"
                    data-gramm_editor="false"
                    data-enable-grammarly="false"
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border mr-4 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-[#E8C547] bg-[#E8C547]' : 'border-[#D5DAE3]'
                  }`}>
                    {isSelected && (
                      <CheckCircle className="w-3.5 h-3.5 text-[#1B2240]" />
                    )}
                  </div>
                  <span className="flex-1">{option.text}</span>
                </motion.label>
              )
            })}
          </div>
        )

      case 'ESSAY':
        return (
          <div className="space-y-3" translate="no">
            <textarea
              value={userAnswer || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder={tObj.quiz.writeAnswerHere()}
              rows={6}
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
              className="w-full p-4 bg-white border border-[#D5DAE3] focus:outline-none focus:border-[#203a8a] resize-vertical text-[#1B2240]"
            />
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">
              {tObj.quiz.manuallyEvaluated()}
            </p>
          </div>
        )

      default:
        return <p className="text-red-500">{tObj.quiz.unsupportedQuestionType()}</p>
    }
  }

  // Vérifier si tentative verrouillée (tentatives vraiment épuisées, pas de tentative en cours à reprendre)
  // Ne pas verrouiller si :
  // - Les tentatives sont illimitées (maxAttempts === null)
  // - L'utilisateur a une tentative en cours qu'il peut reprendre
  if (quiz && initialAttemptLock && !isUnlimitedAttempts && !ongoingAttempt) {
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md mx-auto px-4"
        >
          <div className="bg-white border border-[#D5DAE3] p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D5DAE3] mb-6">
              <Lock className="w-7 h-7 text-[#203a8a]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-3">— Accès restreint</p>
            <h2 className="font-serif text-3xl text-[#1B2240] mb-4">{tObj.quiz.quizLocked()}</h2>
            <p className="text-[#6B7280] mb-3 leading-relaxed">
              {tObj.quiz.attemptsExhausted() || 'Toutes les tentatives ont été utilisées pour ce quiz.'}
            </p>
            <p className="text-sm text-[#9CA3AF] mb-8">
              {tObj.quiz.contactInstructor() || 'Veuillez contacter votre instructeur pour plus d\'informations.'}
            </p>
            <Link to={`/courses/${courseId}/modules/${moduleId}`}>
              <Button className="bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-none">
                {tObj.quiz.backToModule()}
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  if (loading || courseLoading) {
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D5DAE3] mb-4">
            <Loader2 className="w-6 h-6 text-[#203a8a] animate-spin" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280]">{tObj.quiz.loadingQuiz()}</p>
        </div>
      </div>
    )
  }

  if (error) {
    // Vérifier si c'est une erreur 403 (Forbidden) - tous les blocs ne sont pas complétés
    const isForbidden = error?.status === 403 || error?.statusCode === 403 || 
                       error?.message?.includes('403') || 
                       error?.message?.toLowerCase().includes('forbidden') ||
                       error?.message?.toLowerCase().includes('blocs') ||
                       error?.message?.toLowerCase().includes('compléter')
    
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto px-4"
        >
          <div className="bg-white border border-[#D5DAE3] p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D5DAE3] mb-6">
              <AlertCircle className="w-7 h-7 text-[#203a8a]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-3">— Erreur</p>
            <h2 className="font-serif text-3xl text-[#1B2240] mb-4">
              {isForbidden ? tObj.quiz.quizLocked() : tObj.quiz.errorTitle()}
            </h2>
            <p className="text-[#6B7280] mb-8 leading-relaxed">
              {isForbidden 
                ? tObj.quiz.mustCompleteAllBlocks()
                : error.message || tObj.quiz.errorMessage()}
            </p>
            <Link to={`/courses/${courseId}/modules/${moduleId}`}>
              <Button className="bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-none">{tObj.quiz.backToModule()}</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto px-4 text-center"
        >
          <div className="bg-white border border-[#D5DAE3] p-10">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-3">— Indisponible</p>
            <h2 className="font-serif text-3xl text-[#1B2240] mb-3">{tObj.quiz.noQuizAvailable()}</h2>
            <p className="text-[#6B7280] mb-8">{tObj.quiz.noQuizDesc()}</p>
            <Link to={`/courses/${courseId}/modules/${moduleId}`}>
              <Button className="bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-xs px-8 py-6 rounded-none">{tObj.quiz.backToModule()}</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  const isPassed = apiResult ? apiResult.isPassed : score >= (quiz?.passingScore || 0)
  // Si maxAttempts est null, les tentatives sont illimitées, donc maxAttemptsReached ne peut jamais être true
  const maxAttemptsReached = !isUnlimitedAttempts && (
    apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) || 
    (attemptsInfo && !hasAttemptsAvailable && !ongoingAttempt)
  )
  const canRetake = hasAttemptsAvailable

  const currentQuestion = randomizedSanitizedQuestions?.[currentQuestionIndex]

  // Protection d'accès avec le nouveau système centralisé
  return (
    <AccessGuard
      resourceType="quiz"
      resourceId={quiz?.id}
      course={course}
      showLoading={loading || courseLoading}
      redirectOnBlock={false}
    >
    <div className="min-h-screen bg-[#FAFBFD] pt-20 pb-12">
      {/* Fixed Timer Bar - Only visible during quiz */}
      <AnimatePresence>
      {(quizState === 'in-progress' || quizState === 'submitting') && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-16 sm:top-20 left-0 right-0 z-40 bg-white border-b border-[#D5DAE3]"
        >
          <div className="max-w-4xl mx-auto px-3 sm:px-6 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto min-w-0">
                <h3 className="font-serif text-base sm:text-lg text-[#1B2240] truncate max-w-full sm:max-w-md">{getReadableQuizTitle()}</h3>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
                  {t('quiz.questionOf', { current: currentQuestionIndex + 1, total: randomizedSanitizedQuestions?.length || 0 })}
                </div>
              </div>
              {quiz?.timeLimit > 0 && (
                <div className={`flex items-center gap-2 px-4 py-2 border font-mono text-base font-medium ${
                  timeRemaining <= 300 ? 'border-red-400 text-red-600 animate-pulse' : 
                  timeRemaining <= 600 ? 'border-[#E8C547] text-[#A77F00]' : 
                  'border-[#203a8a] text-[#203a8a]'
                }`}>
                  <Clock className="w-4 h-4" />
                  {formatTime(timeRemaining)}
                </div>
              )}
            </div>
            {/* Progress bar */}
            <div className="mt-3">
              <div className="w-full bg-[#F3F4F6] h-[2px] overflow-hidden">
                <motion.div 
                  className="bg-[#E8C547] h-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentQuestionIndex + 1) / (randomizedSanitizedQuestions?.length || 1)) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
      
      <div className={`max-w-4xl mx-auto px-3 sm:px-6 ${
        (quizState === 'in-progress' || quizState === 'submitting') ? 'pt-20 sm:pt-24' : ''
      }`}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-6"
        >
          <Link to={`/courses/${courseId}/modules/${moduleId}`} className="group flex items-center text-[10px] uppercase tracking-[0.25em] text-[#6B7280] hover:text-[#203a8a] transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="hidden sm:inline">{tObj.quiz.backToModule()}</span>
            <span className="sm:hidden">Retour</span>
          </Link>
          {(quizState === 'in-progress' || quizState === 'submitting') && (
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
                {t('quiz.questionOf', { current: currentQuestionIndex + 1, total: randomizedSanitizedQuestions?.length || 0 })}
              </div>
              {quiz?.timeLimit > 0 && (
                <div className={`flex items-center gap-2 px-3 py-1.5 border ${
                  timeRemaining <= 300 ? 'border-red-400 text-red-600' : 
                  timeRemaining <= 600 ? 'border-[#E8C547] text-[#A77F00]' : 
                  'border-[#203a8a] text-[#203a8a]'
                }`}>
                  <Clock className="w-3.5 h-3.5" />
                  <span className="font-mono text-sm font-medium">{formatTime(timeRemaining)}</span>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {quizState === 'intro' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            {/* Hero éditorial */}
            <div className="relative bg-[#203a8a] border border-[#D5DAE3] overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-[#E8C547]" />
              <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)`, backgroundSize: '24px 24px' }} />
              <div className="relative px-6 sm:px-10 md:px-14 py-12 md:py-16 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="w-4 h-4 text-[#E8C547]" />
                  <p className="text-[10px] uppercase tracking-[0.35em] text-[#E8C547] font-medium">— Évaluation finale du module</p>
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-5 text-white" style={{ color: 'white' }}>
                  {getReadableQuizTitle()}
                </h1>
                {quiz.description && (
                  <p className="text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed mb-8">
                    {quiz.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 text-white/80">
                    <BookOpen className="w-4 h-4 text-[#E8C547]" />
                    <span className="text-[10px] uppercase tracking-[0.25em]">{quiz.questions?.length || 0} {tObj.quiz.questions()}</span>
                  </div>
                  <div className="w-px h-3 bg-white/20" />
                  <div className="flex items-center gap-2 text-white/80">
                    <Clock className="w-4 h-4 text-[#E8C547]" />
                    <span className="text-[10px] uppercase tracking-[0.25em]">{quiz.timeLimit != null ? quiz.timeLimit : '∞'} {tObj.quiz.minutes()}</span>
                  </div>
                  <div className="w-px h-3 bg-white/20" />
                  <div className="flex items-center gap-2 text-white/80">
                    <Award className="w-4 h-4 text-[#E8C547]" />
                    <span className="text-[10px] uppercase tracking-[0.25em]">{t('quiz.requiredScore', { score: quiz.passingScore })}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistiques éditoriales */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#D5DAE3] bg-white">
              {[
                { label: tObj.quiz.questions(), value: quiz.questions?.length || 0, Icon: BookOpen },
                { label: tObj.quiz.minutes() || 'Durée', value: quiz.timeLimit != null ? `${quiz.timeLimit}` : '∞', suffix: quiz.timeLimit != null ? 'min' : '', Icon: Clock },
                { label: 'Score requis', value: `${quiz.passingScore}`, suffix: '%', Icon: Award },
                { label: tObj.quiz.attempt() || 'Tentatives', value: `${totalAttempts}`, suffix: ` / ${isUnlimitedAttempts ? '∞' : (quiz.maxAttempts || 1)}`, Icon: RefreshCcw },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                  className={`p-6 ${i < 3 ? 'border-b lg:border-b-0 lg:border-r border-[#D5DAE3]' : ''} ${i === 0 ? 'border-r' : ''} ${i === 1 ? 'border-b lg:border-b-0' : ''} ${i === 2 ? 'border-r' : ''}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <stat.Icon className="w-5 h-5 text-[#203a8a]" />
                    <div className="w-6 h-px bg-[#E8C547]" />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-2">{stat.label}</p>
                  <p className="font-serif text-3xl text-[#1B2240]">
                    {stat.value}<span className="text-lg text-[#6B7280] ml-1">{stat.suffix}</span>
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Tentatives — éditorial */}
            {attemptsInfo && (
              <div className="bg-white border border-[#D5DAE3]">
                <div className="px-6 py-4 border-b border-[#D5DAE3] flex items-center gap-3">
                  <RefreshCcw className="w-4 h-4 text-[#203a8a]" />
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#1B2240] font-medium">Informations sur les tentatives</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {[
                    { label: 'Tentatives totales', value: totalAttempts },
                    { label: tObj.quiz.remaining() || 'Restantes', value: remainingAttempts === Infinity ? '∞' : remainingAttempts },
                    { label: 'Maximum', value: isUnlimitedAttempts ? '∞' : (quiz.maxAttempts || 1) },
                  ].map((item, i) => (
                    <div key={i} className={`p-6 ${i < 2 ? 'md:border-r border-[#D5DAE3]' : ''} ${i < 2 ? 'border-b md:border-b-0' : ''}`}>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-2">{item.label}</p>
                      <p className="font-serif text-3xl text-[#1B2240]">{item.value}</p>
                    </div>
                  ))}
                </div>
                {ongoingAttempt && (
                  <div className="px-6 py-4 border-t border-[#D5DAE3] bg-[#FAFBFD] flex items-start gap-3">
                    <div className="w-1 self-stretch bg-[#E8C547]" />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#1B2240] font-medium mb-1 flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {tObj.quiz.ongoingAttempt() || 'Une tentative est en cours'}
                      </p>
                      {ongoingAttempt.startedAt && (
                        <p className="text-xs text-[#6B7280]">
                          {tObj.quiz.startedAt() || 'Démarrée le'} — {new Date(ongoingAttempt.startedAt).toLocaleString('fr-FR', {
                            day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Instructions — éditorial */}
            {quiz.instructions && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="bg-white border border-[#D5DAE3] p-6 sm:p-8 flex items-start gap-5"
              >
                <div className="flex items-center justify-center w-10 h-10 border border-[#D5DAE3] flex-shrink-0">
                  <HelpCircle className="w-4 h-4 text-[#203a8a]" />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-2">— Consignes</p>
                  <h3 className="font-serif text-xl text-[#1B2240] mb-3">{tObj.quiz.instructions()}</h3>
                  <p className="text-[#4B5563] leading-relaxed">{quiz.instructions}</p>
                </div>
              </motion.div>
            )}

            {/* Bouton démarrage éditorial */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="bg-white border border-[#D5DAE3] p-8 sm:p-12 text-center"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-3">— Prêt à commencer</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#1B2240] mb-3">L'épreuve commence ici</h3>
              <p className="text-[#6B7280] mb-8 max-w-lg mx-auto leading-relaxed">
                Assurez-vous d'être dans un environnement calme et de disposer du temps nécessaire.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={startQuiz}
                disabled={(!isUnlimitedAttempts && !hasAttemptsAvailable) || initialAttemptLock || startingQuiz}
                className="inline-flex items-center justify-center gap-3 min-w-[260px] bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.25em] text-xs px-10 py-5 disabled:bg-[#9CA3AF] disabled:cursor-not-allowed transition-colors"
              >
                {startingQuiz ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {tObj.quiz.submitting() || 'Démarrage...'}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    {initialAttemptLock
                      ? (tObj.quiz.quizLocked() || 'Quiz verrouillé')
                      : (tObj.quiz.startQuiz() || 'Commencer le quiz')}
                  </>
                )}
              </motion.button>
              {!isUnlimitedAttempts && (!hasAttemptsAvailable || initialAttemptLock) && (
                <div className="mt-6 inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <p className="text-[10px] uppercase tracking-[0.25em] font-medium">
                    {tObj.quiz.attemptsExhausted() || 'Toutes les tentatives ont été utilisées'}
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {quizState === 'in-progress' && currentQuestion && (
            <motion.div
              key={currentQuestion.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white border border-[#D5DAE3]"
            >
              <div className="px-6 sm:px-10 py-8 sm:py-10 border-b border-[#D5DAE3]">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium">
                    — {t('quiz.questionNumber', { number: currentQuestionIndex + 1 })}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] px-2.5 py-1 border border-[#D5DAE3]">{currentQuestion.type}</span>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#1B2240] px-2.5 py-1 border border-[#E8C547] bg-[#FFF9E5]">{currentQuestion.points} pts</span>
                  </div>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-[#1B2240] leading-snug">
                  {currentQuestion.question}
                </h2>
                {currentQuestion.isRequired && (
                  <p className="text-red-600 text-xs uppercase tracking-[0.2em] flex items-center gap-2 mt-3">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {tObj.quiz.requiredQuestion()}
                  </p>
                )}
              </div>
              <div className="px-6 sm:px-10 py-8">
                {renderQuestion(currentQuestion)}

                <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#D5DAE3]">
                  <Button
                    onClick={goToPreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                    className="bg-transparent hover:bg-[#FAFBFD] border border-[#D5DAE3] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] px-5 py-5 rounded-none flex items-center gap-2 disabled:opacity-40"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    {tObj.quiz.previous()}
                  </Button>

                  <div className="hidden sm:flex items-center gap-1.5">
                    {randomizedSanitizedQuestions?.map((question, index) => (
                      <div
                        key={question.id || index}
                        className={`h-1 transition-all ${
                          index === currentQuestionIndex
                            ? 'w-6 bg-[#203a8a]'
                            : answers[question.id]
                            ? 'w-3 bg-[#E8C547]'
                            : 'w-3 bg-[#D5DAE3]'
                        }`}
                      />
                    ))}
                  </div>

                  {currentQuestionIndex === (randomizedSanitizedQuestions?.length || 0) - 1 ? (
                    <Button
                      onClick={handleQuizSubmit}
                      disabled={submitting}
                      className="bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] px-5 py-5 rounded-none flex items-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          {tObj.quiz.submitting()}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-3.5 h-3.5" />
                          {tObj.quiz.finishQuiz()}
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={goToNextQuestion}
                      className="bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] px-5 py-5 rounded-none flex items-center gap-2"
                    >
                      {tObj.quiz.next()}
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
        )}

        {quizState === 'submitting' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white border border-[#D5DAE3] p-12 text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D5DAE3] mb-6">
              <Loader2 className="w-6 h-6 text-[#203a8a] animate-spin" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-3">— Soumission</p>
            <h3 className="font-serif text-2xl text-[#1B2240] mb-2">{tObj.quiz.submittingTitle()}</h3>
            <p className="text-[#6B7280]">{tObj.quiz.submittingDesc()}</p>
          </motion.div>
        )}

        {quizState === 'completed' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            {/* Carte résultats principale — éditorial */}
            <div className="bg-white border border-[#D5DAE3] overflow-hidden">
              <div className="relative bg-[#203a8a] px-6 sm:px-10 py-10 text-white overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-[#E8C547]" />
                <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)`, backgroundSize: '24px 24px' }} />
                <div className="flex items-center gap-3 mb-4">
                  {maxAttemptsReached ? (
                    <XCircle className="w-4 h-4 text-orange-300" />
                  ) : isPassed ? (
                    <CheckCircle className="w-4 h-4 text-[#E8C547]" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-300" />
                  )}
                  <p className="text-[10px] uppercase tracking-[0.35em] text-[#E8C547] font-medium">— {tObj.quiz.results()}</p>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-white leading-snug mb-2" style={{ color: 'white' }}>{getReadableQuizTitle()}</h2>
                <p className="text-white/70">
                  {maxAttemptsReached 
                    ? tObj.quiz.maxAttemptsReached()
                    : isPassed 
                    ? tObj.quiz.congratulations()
                    : tObj.quiz.scoreNotReached()}
                </p>
              </div>
              <div className="px-6 sm:px-10 py-10">
                <div className="text-center mb-10">
                  {maxAttemptsReached ? (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280] mb-3">— {tObj.quiz.quizNotSubmitted()}</p>
                      <p className="font-serif text-7xl text-orange-500 mb-4">⚠</p>
                      <span className="inline-block text-[10px] uppercase tracking-[0.3em] px-5 py-2 border border-orange-400 text-orange-600">
                        {tObj.quiz.attemptsExhausted()}
                      </span>
                    </>
                  ) : (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280] mb-3">— {tObj.quiz.yourScore()}</p>
                      <motion.p
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className={`font-serif text-7xl sm:text-8xl mb-4 ${isPassed ? 'text-[#1B2240]' : 'text-red-600'}`}
                      >
                        {score}<span className="text-4xl text-[#6B7280]">%</span>
                      </motion.p>
                      <span className={`inline-block text-[10px] uppercase tracking-[0.3em] px-5 py-2 border ${isPassed ? 'border-[#E8C547] bg-[#FFF9E5] text-[#1B2240]' : 'border-red-400 text-red-600'}`}>
                        {isPassed ? tObj.quiz.passed() : tObj.quiz.failed()}
                      </span>
                    </>
                  )}
                  
                  {/* Barre de progression */}
                  <div className="mt-8 w-full max-w-md mx-auto">
                    <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-2">
                      <span>0%</span>
                      <span className="text-[#1B2240]">{t('quiz.requiredScoreLabel', { score: quiz.passingScore })}</span>
                      <span>100%</span>
                    </div>
                    <div className="relative w-full bg-[#F3F4F6] h-1">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(score, 100)}%` }}
                        transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className={`h-1 ${isPassed ? 'bg-[#E8C547]' : 'bg-red-500'}`}
                      />
                      <div 
                        className="absolute top-[-6px] w-px h-4 bg-[#1B2240]"
                        style={{ left: `${quiz.passingScore}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-[#D5DAE3]">
                  {[
                    { label: tObj.quiz.questions(), value: quiz.questions?.length || 0 },
                    { label: tObj.quiz.timeSpent(), value: apiResult ? `${Math.floor(apiResult.timeSpent / 60)}:${(apiResult.timeSpent % 60).toString().padStart(2, '0')}` : 'N/A' },
                    { label: tObj.quiz.attempt(), value: attemptsMade },
                    { label: tObj.quiz.remaining(), value: isUnlimitedAttempts ? '∞' : (quiz.maxAttempts - attemptsMade) },
                  ].map((s, i) => (
                    <div key={i} className="p-5 border-r border-b border-[#D5DAE3] text-center">
                      <p className="font-serif text-2xl text-[#1B2240] mb-1">{s.value}</p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Stats détaillées */}
                {apiResult && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#D5DAE3] mt-6">
                    <div className="p-5 text-center border-b md:border-b-0 md:border-r border-[#D5DAE3]">
                      <p className="font-serif text-xl text-[#203a8a] mb-1">
                        {apiResult.score % 1 === 0 ? apiResult.score : parseFloat(apiResult.score.toFixed(2))}<span className="text-[#6B7280]">/{apiResult.maxScore}</span>
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{tObj.quiz.pointsObtained()}</p>
                    </div>
                    <div className="p-5 text-center border-b md:border-b-0 md:border-r border-[#D5DAE3]">
                      <p className="font-serif text-xl text-[#1B2240] mb-1">
                        {apiResult.answers ? apiResult.answers.filter(a => a.isCorrect).length : 0}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{tObj.quiz.correctAnswers()}</p>
                    </div>
                    <div className="p-5 text-center">
                      <p className="font-serif text-xl text-red-600 mb-1">
                        {apiResult.answers ? apiResult.answers.filter(a => !a.isCorrect).length : 0}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{tObj.quiz.errors()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Certificat — éditorial */}
            {apiResult?.certificate && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="relative bg-white border border-[#E8C547]"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#E8C547]" />
                <div className="px-6 sm:px-10 py-10 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 border border-[#E8C547] bg-[#FFF9E5] mb-6">
                    <Award className="w-7 h-7 text-[#E8C547]" />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.35em] text-[#E8C547] font-medium mb-3">— {tObj.quiz.congratulationsCertificate()}</p>
                  <h3 className="font-serif text-3xl text-[#1B2240] mb-3">{tObj.quiz.completionCertificate()}</h3>
                  <p className="text-[#4B5563] text-lg mb-6">{apiResult.certificate.course?.title || tObj.quiz.courseCompleted()}</p>
                  <div className="inline-block px-6 py-4 border border-[#D5DAE3] mb-8">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-1">{tObj.quiz.certificateNumber()}</p>
                    <p className="font-mono text-sm text-[#1B2240] font-medium">{apiResult.certificate.certificateNumber}</p>
                    <div className="w-12 h-px bg-[#E8C547] mx-auto my-3" />
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
                      {tObj.quiz.issuedOn()} {new Date(apiResult.certificate.issuedAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => navigate('/profile?tab=achievements')}
                      className="bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] px-6 py-5 rounded-none flex items-center justify-center gap-2"
                    >
                      <Award className="w-3.5 h-3.5" />
                      {tObj.quiz.viewMyCertificates()}
                    </Button>
                    <Button
                      onClick={async () => { await handleDownloadCertificate(apiResult.certificate) }}
                      className="bg-transparent hover:bg-[#FAFBFD] border border-[#D5DAE3] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] px-6 py-5 rounded-none flex items-center justify-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {tObj.quiz.downloadPDF()}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Éligibilité certificat — éditorial */}
            {apiResult?.certificateEligibility && !apiResult.certificate && (
              <div className="bg-white border border-[#D5DAE3]">
                <div className="px-6 py-4 border-b border-[#D5DAE3] flex items-center gap-3">
                  <Award className="w-4 h-4 text-[#203a8a]" />
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#1B2240] font-medium">— {tObj.quiz.certificateProgress()}</p>
                </div>
                <div className="px-6 py-8">
                  {apiResult.certificateEligibility.isEligible ? (
                    <div className="text-center">
                      <p className="font-serif text-xl text-[#1B2240] mb-2">{tObj.quiz.eligibleForCertificate()}</p>
                      <p className="text-sm text-[#6B7280]">{tObj.quiz.completeAllModules()}</p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      <div className="text-center">
                        <p className="font-serif text-2xl text-[#1B2240] mb-3">
                          {t('quiz.completedModules', { completed: apiResult.certificateEligibility.completedModules, total: apiResult.certificateEligibility.totalModules })}
                        </p>
                        <div className="w-full bg-[#F3F4F6] h-1 mb-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(apiResult.certificateEligibility.completedModules / apiResult.certificateEligibility.totalModules) * 100}%` }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="bg-[#E8C547] h-1"
                          />
                        </div>
                      </div>
                      {apiResult.certificateEligibility.missingQuizzes && apiResult.certificateEligibility.missingQuizzes.length > 0 && (
                        <div className="flex items-start gap-3 px-4 py-3 border border-[#E8C547] bg-[#FFF9E5]">
                          <div className="w-1 self-stretch bg-[#E8C547]" />
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-[#1B2240] font-medium mb-1">
                              {t('quiz.remainingModules', { count: apiResult.certificateEligibility.missingQuizzes.length })}
                            </p>
                            <p className="text-xs text-[#6B7280]">
                              {t('quiz.modulesRemaining', { count: apiResult.certificateEligibility.missingQuizzes.length })}
                            </p>
                          </div>
                        </div>
                      )}
                      {apiResult.certificateEligibility.failedQuizzes && apiResult.certificateEligibility.failedQuizzes.length > 0 && (
                        <div className="flex items-start gap-3 px-4 py-3 border border-red-300 bg-red-50">
                          <div className="w-1 self-stretch bg-red-500" />
                          <div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-medium mb-1">
                              {t('quiz.quizzesToRetake', { count: apiResult.certificateEligibility.failedQuizzes.length })}
                            </p>
                            <p className="text-xs text-red-600">{tObj.quiz.mustRetakeQuizzes()}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Messages API — éditorial */}
            {apiMessages && apiMessages.length > 0 && (
              <div className={`bg-white border ${apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) ? 'border-red-300' : 'border-[#D5DAE3]'}`}>
                <div className="flex items-stretch">
                  <div className={`w-1 ${apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) ? 'bg-red-500' : 'bg-[#E8C547]'}`} />
                  <div className="px-6 py-5 flex-1">
                    <p className={`text-[10px] uppercase tracking-[0.3em] font-medium mb-2 flex items-center gap-2 ${apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) ? 'text-red-700' : 'text-[#1B2240]'}`}>
                      {apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) && <XCircle className="w-3.5 h-3.5" />}
                      — {apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) ? tObj.quiz.attemptsExhaustedTitle() : tObj.quiz.messages()}
                    </p>
                    {apiMessages.map((message, index) => (
                      <p key={index} className="text-sm text-[#4B5563]">{message}</p>
                    ))}
                    {apiMessages.some(msg => msg.includes('nombre maximum de tentatives')) && (
                      <p className="text-sm mt-2 text-red-700 font-medium">{tObj.quiz.cannotRetake()}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Révisions des réponses — éditorial */}
            {apiResult && apiResult.answers && (
              <div className="bg-white border border-[#D5DAE3]">
                <div className="px-6 sm:px-10 py-6 border-b border-[#D5DAE3]">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-medium mb-2">— Révisions</p>
                  <h3 className="font-serif text-2xl text-[#1B2240] mb-1">{tObj.quiz.reviewAnswers()}</h3>
                  <p className="text-sm text-[#6B7280]">{tObj.quiz.reviewDesc()}</p>
                </div>
                <div className="divide-y divide-[#D5DAE3]">
                  {apiResult.answers.map((answer, index) => (
                    <motion.div
                      key={answer.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.3 }}
                      className="flex items-stretch"
                    >
                      <div className={`w-1 ${answer.isCorrect ? 'bg-[#E8C547]' : 'bg-red-500'}`} />
                      <div className="flex-1 px-6 py-5">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <h4 className="font-serif text-base text-[#1B2240] leading-snug">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mr-2">{t('quiz.questionNumber', { number: index + 1 })}</span>
                            {answer.question.question}
                          </h4>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {answer.isCorrect ? (
                              <CheckCircle className="w-4 h-4 text-[#E8C547]" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-500" />
                            )}
                            <span className={`text-[10px] uppercase tracking-[0.25em] font-medium ${answer.isCorrect ? 'text-[#1B2240]' : 'text-red-600'}`}>
                              {answer.points % 1 === 0 ? answer.points : parseFloat(answer.points.toFixed(2))}/{answer.maxPoints} pts
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1 text-sm text-[#4B5563]">
                          <p><span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mr-2">{tObj.quiz.yourAnswer()}</span> {getReadableAnswerFromApiResult(answer)}</p>
                          <p><span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mr-2">{tObj.quiz.timeSpentLabel()}</span> {answer.timeSpent}s</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {submitError && (
              <div className="bg-white border border-red-300">
                <div className="flex items-stretch">
                  <div className="w-1 bg-red-500" />
                  <div className="px-6 py-5">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-red-700 font-medium mb-1">— {tObj.quiz.submissionError()}</p>
                    <p className="text-sm text-[#4B5563]">{tObj.quiz.submissionErrorDesc()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons d'action — éditorial */}
            <div className="bg-white border border-[#D5DAE3] p-6 sm:p-8">
              <div className="flex flex-col gap-3">
                {isPassed ? (
                  <>
                    {apiResult?.certificate && (
                      <Button
                        onClick={() => navigate('/profile?tab=achievements')}
                        className="w-full bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                      >
                        <Award className="w-4 h-4" />
                        {tObj.quiz.viewMyCertificates()}
                      </Button>
                    )}
                    {apiResult?.courseProgress?.courseCompleted && !apiResult?.certificate && (
                      <div className="text-center p-6 border border-[#E8C547] bg-[#FFF9E5]">
                        <Award className="w-10 h-10 text-[#E8C547] mx-auto mb-3" />
                        <p className="font-serif text-lg text-[#1B2240] mb-1">{tObj.quiz.allModulesCompletedMessage()}</p>
                        <p className="text-sm text-[#6B7280]">{tObj.quiz.returnToCoursesList()}</p>
                      </div>
                    )}
                    {apiResult?.courseProgress?.nextModule && (() => {
                      const nextModule = apiResult.courseProgress.nextModule
                      if (!nextModule?.id) { console.error('nextModule.id is missing', nextModule); return null }
                      const targetCourseId = course?.id || courseId
                      return (
                        <Button
                          onClick={() => navigate(`/courses/${targetCourseId}/modules/${nextModule.id}`)}
                          className="w-full bg-[#203a8a] hover:bg-[#1B2240] text-white uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          {t('quiz.continueToNextModule', { title: nextModule.title || 'Module suivant' })}
                        </Button>
                      )
                    })()}
                    {!apiResult?.courseProgress?.nextModule && !apiResult?.courseProgress?.courseCompleted && (
                      <Button
                        onClick={() => navigate(`/courses/${courseId}`)}
                        className="w-full bg-[#203a8a] hover:bg-[#1B2240] text-white uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {tObj.quiz.continueCourse()}
                      </Button>
                    )}
                  </>
                ) : canRetake ? (
                  <Button
                    onClick={retakeQuiz}
                    className="w-full bg-[#203a8a] hover:bg-[#1B2240] text-white uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                  >
                    <RefreshCcw className="w-4 h-4" />
                    {isUnlimitedAttempts 
                      ? tObj.quiz.retakeQuizUnlimited?.() || 'Retourner au contenu du cours'
                      : t('quiz.retakeQuiz', { remaining: quiz.maxAttempts - attemptsMade })}
                  </Button>
                ) : (
                  <div className="text-center p-6 border border-red-300 bg-red-50">
                    <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <p className="font-serif text-lg text-red-700 mb-2">
                      {maxAttemptsReached ? tObj.quiz.attemptsExhaustedTitle() : tObj.quiz.maxAttemptsReachedTitle()}
                    </p>
                    {!isUnlimitedAttempts && (
                      <p className="text-sm text-red-600">{t('quiz.allAttemptsUsed', { max: quiz.maxAttempts })}</p>
                    )}
                    <p className="text-sm text-red-600 mt-1">{tObj.quiz.contactInstructor()}</p>
                  </div>
                )}
                <Link to={`/courses/${courseId}`} className="w-full">
                  <Button className="w-full bg-transparent hover:bg-[#FAFBFD] border border-[#D5DAE3] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    {tObj.quiz.backToCourse()}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Modal de félicitation — éditorial */}
        <AnimatePresence>
        {showCongratulationsModal && apiResult?.courseProgress?.courseCompleted && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#1B2240]/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCongratulationsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-white border border-[#E8C547] max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#E8C547]" />
              <div className="p-8 sm:p-10 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 border border-[#E8C547] bg-[#FFF9E5] mb-6">
                  <Award className="w-7 h-7 text-[#E8C547]" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#E8C547] font-medium mb-3">— Félicitations</p>
                <h2 className="font-serif text-3xl text-[#1B2240] mb-3">{tObj.quiz.congratulationsTitle()}</h2>
                <p className="text-[#4B5563] leading-relaxed mb-3">
                  {apiResult?.certificate 
                    ? tObj.quiz.courseCompletedWithCertificate()
                    : tObj.quiz.allModulesCompleted()}
                </p>
                {apiResult?.certificate && (
                  <div className="inline-block px-4 py-2 border border-[#D5DAE3] mb-6">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-1">{tObj.quiz.certificateNumber()}</p>
                    <p className="font-mono text-sm text-[#1B2240]">{apiResult.certificate.certificateNumber}</p>
                  </div>
                )}
                <div className="flex flex-col gap-3 mt-6">
                  {apiResult?.certificate && (
                    <>
                      <Button
                        onClick={() => { setShowCongratulationsModal(false); setShowReviewModal(true) }}
                        className="w-full bg-[#E8C547] hover:bg-[#D4B53D] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                      >
                        <Award className="w-4 h-4" />
                        {tObj.quiz.evaluateCourse?.() || "Évaluer le cours"}
                      </Button>
                      <Button
                        onClick={() => { setShowCongratulationsModal(false); navigate('/profile?tab=achievements') }}
                        className="w-full bg-transparent hover:bg-[#FAFBFD] border border-[#E8C547] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                      >
                        <Award className="w-4 h-4" />
                        {tObj.quiz.viewMyCertificates()}
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => { setShowCongratulationsModal(false); navigate(`/courses/${courseId}`) }}
                    className="w-full bg-[#203a8a] hover:bg-[#1B2240] text-white uppercase tracking-[0.2em] text-[10px] py-6 rounded-none flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {tObj.quiz.backToCourse()}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Modal d'évaluation du cours */}
        {showReviewModal && courseId && (
          <CourseReviewModal
            courseId={courseId}
            courseTitle={course?.title}
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            onReviewSubmitted={() => {
              // Après l'évaluation, afficher le preview du certificat
              setShowReviewModal(false)
              setShowCertificatePreview(true)
            }}
            required={true}
          />
        )}

        {/* Preview du certificat après évaluation — éditorial */}
        <AnimatePresence>
        {showCertificatePreview && apiResult?.certificate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#1B2240]/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white border border-[#D5DAE3] p-6 max-w-md w-full"
            >
              <CertificatePreview
                certificate={apiResult.certificate}
                onDownload={async (cert) => { await handleDownloadCertificate(cert) }}
                showEvaluationModal={false}
                onEvaluationComplete={() => {}}
              />
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={() => { setShowCertificatePreview(false); navigate('/profile?tab=achievements') }}
                  className="w-full bg-transparent hover:bg-[#FAFBFD] border border-[#D5DAE3] text-[#1B2240] uppercase tracking-[0.2em] text-[10px] py-6 rounded-none"
                >
                  {tObj.quiz.viewMyCertificates()}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </div>
    </AccessGuard>
  )
}